import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatCentsAsDollars,
  isValidDollarAmountWithLimits,
  isValidNumericAmountWithLimits,
  dollarsToCents,
  centsToDollars,
} from "@/lib/price-utils";
import { calculateLaborCost } from "./PricingCalculator";

interface LaborInputProps {
  laborRate: number; // Expected in cents (from database)
  onLaborRateChange: (value: number) => void; // Should receive cents
  laborHours: number;
  onLaborHoursChange: (value: number | undefined) => void; // Allow undefined for empty fields
  className?: string;
}

/**
 * A component for inputting labor parameters (hourly rate and labor hours)
 */
export function LaborInput({
  laborRate,
  onLaborRateChange,
  laborHours,
  onLaborHoursChange,
  className = "",
}: LaborInputProps) {
  // Use string states for controlled inputs to properly handle editing
  const { t } = useTranslation();
  const [laborRateStr, setLaborRateStr] = useState<string>("");
  const [laborHoursStr, setLaborHoursStr] = useState<string>("");

  // Initialize form values when props change (e.g., template selection)
  // Use separate refs to track initialization for each field
  const laborRateInitialized = useRef(false);
  const laborHoursInitialized = useRef(false);
  const prevLaborRate = useRef(laborRate);
  const prevLaborHours = useRef(laborHours);
  const userIsTyping = useRef(false);

  useEffect(() => {
    // Don't update if user is actively typing
    if (userIsTyping.current) {
      userIsTyping.current = false;
      return;
    }

    // Reset initialization flag if prop actually changed (template switch)
    if (prevLaborRate.current !== laborRate) {
      laborRateInitialized.current = false;
      prevLaborRate.current = laborRate;
    }

    if (!laborRateInitialized.current) {
      // Convert cents to dollars for display
      const displayValue =
        laborRate > 0 ? centsToDollars(laborRate).toFixed(2) : "";
      setLaborRateStr(displayValue);
      laborRateInitialized.current = true;
    }
  }, [laborRate]);

  useEffect(() => {
    // Don't update if user is actively typing
    if (userIsTyping.current) {
      userIsTyping.current = false;
      return;
    }

    // Reset initialization flag if prop actually changed (template switch)
    if (prevLaborHours.current !== laborHours) {
      laborHoursInitialized.current = false;
      prevLaborHours.current = laborHours;
    }

    if (!laborHoursInitialized.current) {
      setLaborHoursStr(laborHours > 0 ? laborHours.toFixed(2) : "");
      laborHoursInitialized.current = true;
    }
  }, [laborHours]);

  // Input handlers with enhanced validation
  const handleLaborRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Use enhanced validation with reasonable max for labor rate ($999.99/hour)
    if (isValidDollarAmountWithLimits(value, 999.99)) {
      const numericValueInDollars = value === "" ? 0 : parseFloat(value);

      userIsTyping.current = true;
      setLaborRateStr(value);
      // Convert dollars to cents before calling the change handler
      onLaborRateChange(dollarsToCents(numericValueInDollars));
    }
  };

  const handleLaborHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Use enhanced validation with reasonable max for labor hours (999.99 hours)
    if (isValidNumericAmountWithLimits(value, 999.99, 2)) {
      userIsTyping.current = true;
      setLaborHoursStr(value);
      // Send undefined for empty values, otherwise parse as number
      onLaborHoursChange(value === "" ? undefined : parseFloat(value));
    }
  };

  // Simple blur handlers to add .00 formatting
  const handleLaborRateBlur = () => {
    if (laborRateStr && !isNaN(parseFloat(laborRateStr))) {
      setLaborRateStr(parseFloat(laborRateStr).toFixed(2));
    }
  };

  const handleLaborHoursBlur = () => {
    if (laborHoursStr && !isNaN(parseFloat(laborHoursStr))) {
      setLaborHoursStr(parseFloat(laborHoursStr).toFixed(2));
    }
  };

  // Calculate labor cost in cents
  const getLaborCostCents = () => {
    return calculateLaborCost(laborRate, laborHours); // laborRate is already in cents
  };

  return (
    <div className={className}>
      <div className="space-y-3">
        {/* Hourly Rate Input */}
        <div className="grid grid-cols-2 gap-4 items-center">
          <Label
            htmlFor="hourly-rate"
            className="text-sm text-muted-foreground"
          >
            {t("components.quotes.laborInput.hourlyRate")}
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-500">$</span>
            </div>
            <Input
              id="hourly-rate"
              type="text"
              value={laborRateStr}
              onChange={handleLaborRateChange}
              onBlur={handleLaborRateBlur}
              placeholder="0.00"
              className="pl-6"
              max="9999.99"
            />
          </div>
        </div>

        {/* Labor Hours Input */}
        <div className="grid grid-cols-2 gap-4 items-center">
          <Label
            htmlFor="labor-hours"
            className="text-sm text-muted-foreground"
          >
            {t("components.quotes.laborInput.laborHours")}
          </Label>
          <Input
            id="labor-hours"
            type="text"
            value={laborHoursStr}
            onChange={handleLaborHoursChange}
            onBlur={handleLaborHoursBlur}
            placeholder="0.00"
            max="9999.99"
          />
        </div>

        {/* Labor Cost Display */}
        <div className="flex justify-between text-sm font-medium pt-2 border-t border-border/50">
          <span className="text-muted-foreground">{t("components.quotes.laborInput.laborTotal")}</span>
          <span>${formatCentsAsDollars(getLaborCostCents())}</span>
        </div>
      </div>
    </div>
  );
}
